import { getErrorSamplingOnResponse, setErrorSamplingOnError } from '@change-corgi/core/errorReporter/common';
import { isPerimeterXHttpResponse } from '@change-corgi/core/perimeterx';

function isCloudFlareError(response: Response) {
	// our graphql middleware adds the x-graphql header
	// if it's missing, it means that the request didn't reach our server
	// and, if there is a cf-ray header, it means that it did reach Cloudflare, which is probably where the request stopped
	// note: x-graphql could also be removed from the response by a proxy, so it's not 100% foolproof
	// if the Change-Api header is present, the response came from our API server, so it's not a CloudFlare error
	return (
		!!response.headers.get('cf-ray') &&
		response.headers.get('x-graphql') !== 'true' &&
		!response.headers.get('Change-Api')
	);
}

function isCloudfrontError(response: Response) {
	// our graphql middleware adds the x-graphql header
	// if it's missing, it means that the request didn't reach our server
	// and, if there is a x-amz-cf-id header, it means that it did reach Cloudfront, which is probably where the request stopped
	// note: x-graphql could also be removed from the response by a proxy, so it's not 100% foolproof
	// if the Change-Api header is present, the response came from our API server, so it's not a Cloudfront error
	return (
		!!response.headers.get('x-amz-cf-id') &&
		response.headers.get('x-graphql') !== 'true' &&
		!response.headers.get('Change-Api')
	);
}

function isRobotsError(response: Response) {
	return response.headers.get('reason') === 'robots.txt denied';
}

function isProxyError(response: Response) {
	// our graphql middleware adds the x-graphql header
	// if it's missing, it means that the request didn't reach our server
	// note: x-graphql could also be removed from the response by a proxy, so it's not 100% foolproof
	return response.headers.get('x-graphql') !== 'true';
}

/**
 * no header from our edge (Cloudfront, Cloudflare) or from our server:
 * the response was made by a proxy outside our network (e.g. email security scanners)
 *
 * only reliable in environments behind Cloudfront: elsewhere, no response has x-amz-cf-id
 */
export function isOffNetworkResponse(response: Response): boolean {
	return (
		!response.headers.get('x-amz-cf-id') &&
		// TODO: remove cf-ray once Cloudflare=>Cloudfront migration is complete
		!response.headers.get('cf-ray') &&
		!response.headers.get('Change-Api') &&
		isProxyError(response)
	);
}

function isCsrfError(response: Response) {
	return response.headers.get('X-Invalid-CSRF') === 'true';
}

function genFetchResponseError(response: Response): Error {
	if (isPerimeterXHttpResponse(response)) {
		return new Error(`GraphQL query blocked by PerimeterX (HTTP ${response.status})`);
	}
	if (isCsrfError(response)) {
		return new Error(`GraphQL query blocked by CSRF (HTTP ${response.status})`);
	}
	if (isRobotsError(response)) {
		return new Error(`GraphQL query blocked by robots.txt (HTTP ${response.status})`);
	}
	if (isCloudFlareError(response)) {
		return new Error(`GraphQL query blocked by CloudFlare (HTTP ${response.status})`);
	}
	if (isCloudfrontError(response)) {
		return new Error(`GraphQL query blocked by Cloudfront (HTTP ${response.status})`);
	}
	// same rule as the sampling of off-network 504s in wrapFetch
	if (isOffNetworkResponse(response)) {
		return new Error(`GraphQL query blocked by Proxy or equivalent (HTTP ${response.status})`);
	}
	return new Error(`HTTP ${response.status} Error when running GraphQL query`);
}

export function getFetchResponseError(response: Response): Error {
	const error = genFetchResponseError(response);
	// keep the sampling decision made by wrapFetch (e.g. for off-network 504s)
	const sampling = getErrorSamplingOnResponse(response);
	if (sampling) setErrorSamplingOnError(error, sampling);
	return error;
}
